import React, { useEffect, useState } from "react"
import { Modal, Input, Typography } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import { useRenameProjectFolderMutation } from "features/Projects/api/useProjectsMutations"

interface RenameFolderModalProps {
  open: boolean
  onClose: () => void
  folderPath: string | null
}

const RenameFolderModal: React.FC<RenameFolderModalProps> = ({
  open,
  onClose,
  folderPath,
}) => {
  const { Title } = Typography
  const renameFolderMutation = useRenameProjectFolderMutation()

  // Only the leaf segment is editable — the parent path stays put. This
  // mirrors typical filesystem rename UX and keeps the input simple.
  const parts = folderPath ? folderPath.split(".") : []
  const parent = parts.slice(0, -1).join(".")
  const leaf = parts[parts.length - 1] ?? ""
  const [name, setName] = useState<string>(leaf)

  useEffect(() => {
    setName(leaf)
  }, [folderPath, leaf])

  const handleSubmit = async () => {
    if (!folderPath) return
    const trimmed = name.trim()
    if (!trimmed) {
      notify.error(
        "Folder name required",
        "Please enter a folder name."
      )
      return
    }
    if (trimmed.includes(".")) {
      notify.error(
        "Invalid folder name",
        "Folder names cannot contain '.'."
      )
      return
    }
    const newPath = parent ? `${parent}.${trimmed}` : trimmed
    if (newPath === folderPath) {
      onClose()
      return
    }
    try {
      await renameFolderMutation.mutateAsync({ oldPath: folderPath, newPath })
      notify.success(
        "Folder renamed",
        `Renamed '${folderPath}' to '${newPath}'.`
      )
      onClose()
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error"
      notify.error("Error renaming folder", message)
    }
  }

  return (
    <Modal
      open={open}
      centered
      footer={false}
      closable={false}
      destroyOnHidden
      onCancel={onClose}
    >
      <Title level={4} style={{ marginTop: "0.5em" }}>
        Rename Folder
      </Title>
      <div className="flex flex-col space-y-3 mt-2">
        {parent && (
          <div className="text-sm text-gray-500">Inside: {parent}</div>
        )}
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Folder name"
          onPressEnter={handleSubmit}
          autoFocus
        />
      </div>
      <div className="flex justify-end space-x-2 mt-4">
        <DataQButton type="secondary" onClick={onClose}>
          Cancel
        </DataQButton>
        <DataQButton
          type="primary"
          onClick={handleSubmit}
          isDisabled={renameFolderMutation.isPending}
        >
          Rename
        </DataQButton>
      </div>
    </Modal>
  )
}

export default RenameFolderModal
